/**
 * Context pour la gestion d'état du traitement de paie
 * Centralise toutes les données et actions liées au traitement de paie
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
} from "react";
import { message } from "antd";
import {
  PayrollPeriod,
  PayrollBatch,
  PayrollVariable,
  PayrollCalculation,
  PayrollEmployee,
  PayrollStatistics,
  PayrollError,
  PayrollPeriodFormData,
  PayrollVariableFormData,
} from "../../../types/hr";
import { payrollAPI } from "../api";
import { PayrollAction, payrollReducer, PayrollState } from "./reducer";

// ===== INTERFACES =====

interface PayrollContextType {
  // État
  state: PayrollState;

  // Actions - Périodes
  loadPeriods: () => Promise<void>;
  createPeriod: (data: PayrollPeriodFormData) => Promise<PayrollPeriod>;
  updatePeriod: (
    id: number,
    data: Partial<PayrollPeriodFormData>
  ) => Promise<void>;
  deletePeriod: (id: number) => Promise<void>;
  setCurrentPeriod: (period: PayrollPeriod | null) => void;

  // Actions - Employés
  loadPayrollEmployees: (periodId: number) => Promise<PayrollEmployee[]>;
  addEmployeesToPeriod: (
    periodId: number,
    employeeIds: number[]
  ) => Promise<void>;
  removeEmployeeFromPeriod: (
    periodId: number,
    employeeId: number
  ) => Promise<void>;

  // Actions - Variables
  loadVariables: (periodId: number) => Promise<void>;
  setEmployeeVariables: (
    periodId: number,
    employeeId: number,
    data: PayrollVariableFormData
  ) => Promise<void>;
  updateEmployeeVariables: (
    periodId: number,
    employeeId: number,
    data: Partial<PayrollVariableFormData>
  ) => Promise<void>;
  bulkSetVariables: (
    periodId: number,
    variables: PayrollVariableFormData[]
  ) => Promise<void>;

  // Actions - Calculs
  calculatePayroll: (periodId: number, employeeIds?: number[]) => Promise<void>;
  loadCalculations: (periodId: number) => Promise<void>;
  validateCalculations: (
    periodId: number,
    employeeIds?: number[]
  ) => Promise<void>;
  reopenCalculations: (periodId: number) => Promise<void>;

  // Actions - Lots
  loadBatch: (batchId: number) => Promise<void>;

  // Actions - Statistiques
  loadStatistics: (periodId: number) => Promise<void>;

  // Utilitaires
  clearErrors: () => void;
  reset: () => void;
}

// ===== ÉTAT INITIAL =====

const initialState: PayrollState = {
  // Données
  periods: [],
  currentPeriod: null,
  currentBatch: null,
  employees: [],
  variables: [],
  calculations: [],
  statistics: null,

  // États de chargement
  loading: {
    periods: false,
    employees: false,
    variables: false,
    calculations: false,
    batch: false,
    statistics: false,
  },

  // Erreurs
  errors: [],

  // Configuration
  config: {
    auto_calculate: true,
    require_validation: true,
    allow_partial_processing: false,
    backup_before_processing: true,
    email_notifications: true,
    generate_reports: true,
  },
};

// ===== CONTEXT =====

const PayrollContext = createContext<PayrollContextType | undefined>(undefined);

// ===== PROVIDER =====

interface PayrollProviderProps {
  children: React.ReactNode;
}

export const PayrollProvider: React.FC<PayrollProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(payrollReducer, initialState);
  const statisticsRequest = useRef(0);
  const employeesRequest = useRef(0);

  // ===== UTILITAIRES =====

  const handleError = useCallback((error: any, action: string) => {
    console.error(`Erreur lors de ${action}:`, error);
    const errorMessage = error.message || `Erreur lors de ${action}`;
    message.error(errorMessage);

    const payrollError: PayrollError = {
      error_type: "system",
      error_code: "API_ERROR",
      error_message: errorMessage,
    };

    dispatch({ type: "ADD_ERROR", error: payrollError });
  }, []);

  const setLoading = useCallback(
    (resource: keyof PayrollState["loading"], loading: boolean) => {
      dispatch({ type: "SET_LOADING", resource, loading });
    },
    []
  );

  // ===== ACTIONS - PÉRIODES =====

  const loadPeriods = useCallback(async () => {
    setLoading("periods", true);
    try {
      const periods = await payrollAPI.getPeriods();
      dispatch({ type: "SET_PERIODS", periods });
    } catch (error) {
      handleError(error, "chargement des périodes");
    } finally {
      setLoading("periods", false);
    }
  }, [setLoading, handleError]);

  const createPeriod = useCallback(
    async (data: PayrollPeriodFormData): Promise<PayrollPeriod> => {
      try {
        const period = await payrollAPI.createPeriod(data);
        dispatch({ type: "ADD_PERIOD", period });
        message.success("Période créée avec succès");
        return period;
      } catch (error) {
        handleError(error, "création de la période");
        throw error;
      }
    },
    [handleError]
  );

  const updatePeriod = useCallback(
    async (id: number, data: Partial<PayrollPeriodFormData>) => {
      try {
        const period = await payrollAPI.updatePeriod(id, data);
        dispatch({ type: "UPDATE_PERIOD", id, period });
        message.success("Période mise à jour avec succès");
      } catch (error) {
        handleError(error, "mise à jour de la période");
        throw error;
      }
    },
    [handleError]
  );

  const deletePeriod = useCallback(
    async (id: number) => {
      try {
        await payrollAPI.deletePeriod(id);
        dispatch({ type: "REMOVE_PERIOD", id });
        message.success("Période supprimée avec succès");
      } catch (error) {
        handleError(error, "suppression de la période");
        throw error;
      }
    },
    [handleError]
  );

  const setCurrentPeriod = useCallback((period: PayrollPeriod | null) => {
    dispatch({ type: "SET_CURRENT_PERIOD", period });
  }, []);

  // ===== ACTIONS - EMPLOYÉS =====

  const loadPayrollEmployees = useCallback(
    async (periodId: number) => {
      const request = ++employeesRequest.current;
      setLoading("employees", true);
      try {
        const employees = await payrollAPI.getPayrollEmployees(periodId);
        if (request === employeesRequest.current) dispatch({ type: "SET_EMPLOYEES", employees });
        return employees;
      } catch (error) {
        if (request === employeesRequest.current) dispatch({ type: "SET_EMPLOYEES", employees: [] });
        handleError(error, "chargement des employés");
        throw error;
      } finally {
        if (request === employeesRequest.current) setLoading("employees", false);
      }
    },
    [setLoading, handleError]
  );

  const addEmployeesToPeriod = useCallback(
    async (periodId: number, employeeIds: number[]) => {
      try {
        await payrollAPI.addEmployeesToPeriod(periodId, employeeIds);
        await loadPayrollEmployees(periodId);
        message.success(
          `${employeeIds.length} employé(s) ajouté(s) à la période`
        );
      } catch (error) {
        handleError(error, "ajout d'employés à la période");
        throw error;
      }
    },
    [loadPayrollEmployees, handleError]
  );

  const removeEmployeeFromPeriod = useCallback(
    async (periodId: number, employeeId: number) => {
      try {
        await payrollAPI.removeEmployeeFromPeriod(periodId, employeeId);
        dispatch({ type: "REMOVE_EMPLOYEE", employeeId });
        message.success("Employé retiré de la période");
      } catch (error) {
        handleError(error, "retrait de l'employé de la période");
        throw error;
      }
    },
    [handleError]
  );

  // ===== ACTIONS - VARIABLES =====

  const loadVariables = useCallback(
    async (periodId: number) => {
      setLoading("variables", true);
      try {
        const variables = await payrollAPI.getVariables(periodId);
        dispatch({ type: "SET_VARIABLES", variables });
      } catch (error) {
        handleError(error, "chargement des variables");
        throw error;
      } finally {
        setLoading("variables", false);
      }
    },
    [setLoading, handleError]
  );

  const setEmployeeVariables = useCallback(
    async (
      periodId: number,
      employeeId: number,
      data: PayrollVariableFormData
    ) => {
      try {
        const variable = await payrollAPI.setEmployeeVariables(
          periodId,
          employeeId,
          data
        );
        dispatch({ type: "SET_EMPLOYEE_VARIABLES", employeeId, variable });
        message.success("Variables saisies avec succès");
      } catch (error) {
        handleError(error, "saisie des variables");
        throw error;
      }
    },
    [handleError]
  );

  const updateEmployeeVariables = useCallback(
    async (
      periodId: number,
      employeeId: number,
      data: Partial<PayrollVariableFormData>
    ) => {
      try {
        const variable = await payrollAPI.updateEmployeeVariables(
          periodId,
          employeeId,
          data
        );
        dispatch({ type: "SET_EMPLOYEE_VARIABLES", employeeId, variable });
        message.success("Variables mises à jour avec succès");
      } catch (error) {
        handleError(error, "mise à jour des variables");
        throw error;
      }
    },
    [handleError]
  );

  const bulkSetVariables = useCallback(
    async (periodId: number, variables: PayrollVariableFormData[]) => {
      try {
        await payrollAPI.bulkSetVariables(periodId, variables);
        await loadVariables(periodId);
        message.success("Variables en lot saisies avec succès");
      } catch (error) {
        handleError(error, "saisie en lot des variables");
        throw error;
      }
    },
    [loadVariables, handleError]
  );

  // ===== ACTIONS - STATISTIQUES =====

  const loadStatistics = useCallback(
    async (periodId: number) => {
      const request = ++statisticsRequest.current;
      dispatch({ type: "SET_STATISTICS", statistics: null });
      setLoading("statistics", true);
      try {
        const statistics = await payrollAPI.getStatistics(periodId);
        if (request === statisticsRequest.current) dispatch({ type: "SET_STATISTICS", statistics });
      } catch (error) {
        handleError(error, "chargement des statistiques");
        throw error;
      } finally {
        if (request === statisticsRequest.current) setLoading("statistics", false);
      }
    },
    [setLoading, handleError]
  );

  // ===== ACTIONS - CALCULS =====

  const loadCalculations = useCallback(
    async (periodId: number) => {
      setLoading("calculations", true);
      try {
        const calculations = await payrollAPI.getCalculations(periodId);
        dispatch({ type: "SET_CALCULATIONS", calculations });
      } catch (error) {
        handleError(error, "chargement des calculs");
        throw error;
      } finally {
        setLoading("calculations", false);
      }
    },
    [setLoading, handleError]
  );

  const calculatePayroll = useCallback(
    async (periodId: number, employeeIds?: number[]) => {
      setLoading("calculations", true);
      try {
        const batch = await payrollAPI.calculatePayroll(periodId, employeeIds);
        dispatch({ type: "SET_CURRENT_BATCH", batch });
        await loadCalculations(periodId);
        // Recharger les statistiques après les calculs
        await loadStatistics(periodId);
        message.success("Calculs de paie effectués avec succès");
      } catch (error) {
        handleError(error, "calcul de la paie");
        throw error;
      } finally {
        setLoading("calculations", false);
      }
    },
    [setLoading, handleError, loadCalculations, loadStatistics]
  );

  const validateCalculations = useCallback(
    async (periodId: number, employeeIds?: number[]) => {
      try {
        await payrollAPI.validateCalculations(periodId, employeeIds);
        await loadCalculations(periodId);
        // Recharger les statistiques après validation
        await loadStatistics(periodId);
        message.success("Calculs validés avec succès");
      } catch (error) {
        handleError(error, "validation des calculs");
        throw error;
      }
    },
    [loadCalculations, handleError, loadStatistics]
  );

  const reopenCalculations = useCallback(
    async (periodId: number) => {
      setLoading("calculations", true);
      try {
        await payrollAPI.reopenCalculations(periodId);
        await loadPeriods();
        await loadCalculations(periodId);
        await loadStatistics(periodId);
        message.success("Periode rouverte avec succes");
      } catch (error) {
        handleError(error, "reouverture des calculs");
        throw error;
      } finally {
        setLoading("calculations", false);
      }
    },
    [setLoading, handleError, loadPeriods, loadCalculations, loadStatistics]
  );

  // ===== ACTIONS - LOTS =====

  const loadBatch = useCallback(
    async (batchId: number) => {
      setLoading("batch", true);
      try {
        const batch = await payrollAPI.getBatch(batchId);
        dispatch({ type: "SET_CURRENT_BATCH", batch });
      } catch (error) {
        handleError(error, "chargement du lot");
      } finally {
        setLoading("batch", false);
      }
    },
    [setLoading, handleError]
  );

  // ===== UTILITAIRES =====

  const clearErrors = useCallback(() => {
    dispatch({ type: "CLEAR_ERRORS" });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // ===== VALEUR DU CONTEXT =====

  const value: PayrollContextType = {
    state,

    // Périodes
    loadPeriods,
    createPeriod,
    updatePeriod,
    deletePeriod,
    setCurrentPeriod,

    // Employés
    loadPayrollEmployees,
    addEmployeesToPeriod,
    removeEmployeeFromPeriod,

    // Variables
    loadVariables,
    setEmployeeVariables,
    updateEmployeeVariables,
    bulkSetVariables,

    // Calculs
    calculatePayroll,
    loadCalculations,
    validateCalculations,
    reopenCalculations,

    // Lots
    loadBatch,

    // Statistiques
    loadStatistics,

    // Utilitaires
    clearErrors,
    reset,
  };

  return (
    <PayrollContext.Provider value={value}>{children}</PayrollContext.Provider>
  );
};

// ===== HOOK =====

export const usePayrollProcessing = () => {
  const context = useContext(PayrollContext);
  if (context === undefined) {
    throw new Error(
      "usePayrollProcessing must be used within a PayrollProvider"
    );
  }
  return context;
};

export const usePayroll = usePayrollProcessing; // Alias pour compatibilité
export default usePayrollProcessing;
